<app-portal-navigation
  [portalName]="portalName"
  [portalIcon]="portalIcon"
  [portalClass]="portalClass"
  collapseId="createPatientMenu"
  [firstName]="userProfile?.firstName ?? ''"
  [lastName]="userProfile?.lastName ?? ''"
  [primaryItems]="primaryNavigationItems"
  [profileRoute]="profileRoute"
>
</app-portal-navigation>

<div class="container py-4 create-patient-page" style="max-width: 1100px">
  <!-- Hero Banner -->
  <div class="booking-hero card border-0 mb-4">
    <div class="booking-hero__content">
      <div>
        <div class="booking-hero__eyebrow">Patient Registration</div>
        <h2 class="booking-hero__title mb-1">
          {{ isEditMode ? 'Edit Patient File' : 'Register New Patient' }}
        </h2>
        <div class="booking-hero__meta">
          <span
            ><i class="bi bi-person-vcard me-1 text-primary"></i
            >{{
              isEditMode
                ? 'Update demographic and billing details'
                : 'Capture comprehensive identity, medical, and billing information'
            }}</span
          >
        </div>
      </div>
      <div class="booking-hero__status">
        <button class="btn btn-outline-secondary" (click)="cancel()">
          <i class="bi bi-arrow-left me-1"></i> Back
        </button>
      </div>
    </div>
  </div>

  <!-- Prominent Error Alert Banner -->
  @if (errorMessage) {
    <div
      class="alert alert-danger d-flex align-items-start mb-4 p-3 rounded-3 shadow-sm border-danger border-start border-4"
      role="alert"
    >
      <i class="bi bi-exclamation-octagon-fill fs-4 me-3 text-danger flex-shrink-0 mt-1"></i>
      <div class="flex-grow-1">
        <div class="fw-bold text-danger">Action Failed</div>
        <div class="small text-dark mt-1">{{ errorMessage }}</div>
      </div>
      <button
        type="button"
        class="btn-close ms-2"
        (click)="errorMessage = null"
        aria-label="Close error message"
      ></button>
    </div>
  }

  <form [formGroup]="patientForm" (ngSubmit)="submit()" novalidate>
    <!-- Doctor selection for reception users (Q2) -->
    @if (isReception && !isEditMode) {
      <div class="card booking-card mb-4 border-primary-subtle">
        <div
          class="card-header booking-card__header"
          style="
            background: linear-gradient(
              135deg,
              rgba(99, 102, 241, 0.08) 0%,
              rgba(139, 92, 246, 0.08) 100%
            );
          "
        >
          <div>
            <div class="booking-card__eyebrow text-primary">Doctor Assignment</div>
            <h4 class="sub-title mb-0">Select the Doctor for this Patient</h4>
          </div>
          @if (isDoctorListLoading) {
            <span class="spinner-border spinner-border-sm text-primary" role="status"></span>
          }
        </div>
        <div class="card-body p-4">
          @if (linkedDoctors.length === 0 && !isDoctorListLoading) {
            <div class="alert alert-warning d-flex align-items-center gap-2 mb-0">
              <i class="bi bi-exclamation-triangle-fill"></i>
              <span
                >You are not linked to any doctors. Please ask your manager to grant you
                access.</span
              >
            </div>
          } @else {
            <div class="row g-3 align-items-end">
              <div class="col-md-8">
                <label for="doctorSelect" class="form-label"
                  >Assigning Doctor <span class="text-danger">*</span></label
                >
                <select
                  id="doctorSelect"
                  class="form-select form-select-lg"
                  [value]="selectedMembershipId ?? ''"
                  (change)="onDoctorSelected($any($event.target).value)"
                  [disabled]="isDoctorListLoading"
                >
                  <option value="">— Select a doctor —</option>
                  @for (doc of linkedDoctors; track doc.clinicMembershipId) {
                    <option [value]="doc.clinicMembershipId">
                      Dr. {{ doc.doctorFirstName }} {{ doc.doctorLastName }}
                      @if (doc.specialization) {
                        – {{ doc.specialization }}
                      }
                    </option>
                  }
                </select>
              </div>
              <div class="col-md-4">
                @if (selectedMembershipId) {
                  <div class="alert alert-success d-flex align-items-center gap-2 mb-0 py-2">
                    <i class="bi bi-person-check-fill"></i>
                    <small class="fw-semibold">Doctor selected</small>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    }

    <!-- Section 1: Patient Core Details -->
    <div class="card booking-card mb-4">
      <div class="card-header booking-card__header">
        <div>
          <div class="booking-card__eyebrow">Personal Information</div>
          <h4 class="sub-title mb-0">Demographics & Medical Profile</h4>
        </div>
      </div>

      <div class="card-body p-4">
        <div class="row g-3">
          <div class="col-md-3">
            <label for="title" class="form-label">Title</label>
            <select id="title" class="form-select" formControlName="title">
              <option value="">Select Title</option>
              @for (t of titleOptions; track $index) {
                <option [value]="t">{{ t }}</option>
              }
            </select>
          </div>

          <div class="col-md-4">
            <label for="firstName" class="form-label"
              >First Name <span class="text-danger">*</span></label
            >
            <input
              id="firstName"
              class="form-control"
              formControlName="firstName"
              placeholder="e.g. Sarah"
            />
            @if (patientForm.get('firstName')?.touched && patientForm.get('firstName')?.invalid) {
              <div class="text-danger small mt-1">First name is required.</div>
            }
          </div>

          <div class="col-md-5">
            <label for="lastName" class="form-label"
              >Last Name <span class="text-danger">*</span></label
            >
            <input
              id="lastName"
              class="form-control"
              formControlName="lastName"
              placeholder="e.g. Jenkins"
            />
            @if (patientForm.get('lastName')?.touched && patientForm.get('lastName')?.invalid) {
              <div class="text-danger small mt-1">Last name is required.</div>
            }
          </div>

          <div class="col-md-6">
            <label for="email" class="form-label"
              >Email Address <span class="text-danger">*</span></label
            >
            <input
              id="email"
              class="form-control"
              formControlName="email"
              type="email"
              placeholder="patient@example.com"
            />
            @if (patientForm.get('email')?.touched && patientForm.get('email')?.invalid) {
              <div class="text-danger small mt-1">Valid email is required.</div>
            }
          </div>

          <div class="col-md-6">
            <label for="phoneNumber" class="form-label"
              >Phone Number <span class="text-danger">*</span></label
            >
            <input
              id="phoneNumber"
              class="form-control"
              formControlName="phoneNumber"
              placeholder="e.g. 0821234567"
            />
            @if (
              patientForm.get('phoneNumber')?.touched && patientForm.get('phoneNumber')?.invalid
            ) {
              <div class="text-danger small mt-1">Phone number is required.</div>
            }
          </div>

          <div class="col-md-4">
            <label for="gender" class="form-label">Gender <span class="text-danger">*</span></label>
            <select id="gender" class="form-select" formControlName="gender">
              <option value="">Select Gender</option>
              @for (g of genderOptions; track $index) {
                <option [value]="g">{{ g }}</option>
              }
            </select>
            @if (patientForm.get('gender')?.touched && patientForm.get('gender')?.invalid) {
              <div class="text-danger small mt-1">Gender is required.</div>
            }
          </div>

          <div class="col-md-4">
            <label for="dateOfBirth" class="form-label">Date of Birth</label>
            <input
              id="dateOfBirth"
              class="form-control"
              type="date"
              formControlName="dateOfBirth"
            />
          </div>

          <div class="col-md-4">
            <label for="idNumber" class="form-label">ID / Passport Number</label>
            <input
              id="idNumber"
              class="form-control"
              formControlName="idNumber"
              placeholder="National ID"
            />
          </div>

          <div class="col-md-6">
            <label for="maritalStatus" class="form-label">Marital Status</label>
            <select id="maritalStatus" class="form-select" formControlName="maritalStatus">
              <option value="">Select Status</option>
              @for (m of maritalStatusOptions; track $index) {
                <option [value]="m">{{ m }}</option>
              }
            </select>
          </div>

          <div class="col-md-6">
            <label for="employer" class="form-label">Employer</label>
            <input
              id="employer"
              class="form-control"
              formControlName="employer"
              placeholder="Company Name"
            />
          </div>

          <div class="col-md-12">
            <label for="addressLine1" class="form-label">Street Address Line 1</label>
            <input
              id="addressLine1"
              class="form-control"
              formControlName="addressLine1"
              placeholder="Building, Street Name"
            />
          </div>

          <div class="col-md-6">
            <label for="addressLine2" class="form-label">Address Line 2 (Optional)</label>
            <input
              id="addressLine2"
              class="form-control"
              formControlName="addressLine2"
              placeholder="Suite / Unit"
            />
          </div>

          <div class="col-md-6">
            <label for="addressLine3" class="form-label">Address Line 3 (Optional)</label>
            <input
              id="addressLine3"
              class="form-control"
              formControlName="addressLine3"
              placeholder="Suburb / Area"
            />
          </div>

          <div class="col-md-6">
            <label for="city" class="form-label">City</label>
            <input
              id="city"
              class="form-control"
              formControlName="city"
              placeholder="e.g. Cape Town"
            />
          </div>

          <div class="col-md-6">
            <label for="postalCode" class="form-label">Postal Code</label>
            <input
              id="postalCode"
              class="form-control"
              formControlName="postalCode"
              placeholder="e.g. 8001"
            />
          </div>

          <!-- Medical Aid & Health Profile -->
          <div class="col-12 mt-4">
            <div class="section-eyebrow mb-2">Medical Scheme & Health Conditions</div>
          </div>

          <div class="col-md-4">
            <label for="medicalAidScheme" class="form-label">Medical Aid Scheme</label>
            <select id="medicalAidScheme" class="form-select" formControlName="medicalAidScheme">
              <option value="">Select Scheme</option>
              @for (scheme of medicalAidSchemes; track scheme) {
                <option [value]="scheme">{{ scheme }}</option>
              }
            </select>
          </div>

          <div class="col-md-4">
            <label for="medicalAidNumber" class="form-label">Medical Aid Number</label>
            <input
              id="medicalAidNumber"
              class="form-control"
              formControlName="medicalAidNumber"
              placeholder="Membership #"
            />
          </div>

          <div class="col-md-4">
            <label for="medicalAidPlan" class="form-label">Medical Aid Plan</label>
            <input
              id="medicalAidPlan"
              class="form-control"
              formControlName="medicalAidPlan"
              placeholder="Plan / Option"
            />
          </div>

          <div class="col-md-4">
            <label for="bloodType" class="form-label">Blood Type</label>
            <select id="bloodType" class="form-select" formControlName="bloodType">
              <option value="">Select Blood Type</option>
              @for (bloodType of bloodTypes; track bloodType) {
                <option [value]="bloodType">{{ bloodType }}</option>
              }
            </select>
          </div>

          <div class="col-md-4">
            <label for="weight" class="form-label">Weight (kg)</label>
            <input
              id="weight"
              class="form-control"
              type="number"
              formControlName="weight"
              placeholder="e.g. 70"
            />
          </div>

          <div class="col-md-4">
            <label for="chronicIllness" class="form-label">Chronic Illness</label>
            <select id="chronicIllness" class="form-select" formControlName="chronicIllness">
              <option value="">Select Chronic Condition</option>
              @for (chronicIllness of chronicIllnesses; track chronicIllness) {
                <option [value]="chronicIllness">{{ chronicIllness }}</option>
              }
            </select>
          </div>

          <div class="col-md-12">
            <label for="medicalHistorySummary" class="form-label">Medical History Summary</label>
            <textarea
              id="medicalHistorySummary"
              class="form-control"
              formControlName="medicalHistorySummary"
              rows="2"
              placeholder="Relevant past conditions, surgeries, notes..."
            ></textarea>
          </div>

          <div class="col-md-12">
            <label class="form-label">Known Allergies</label>
            @for (a of allergies.controls; track $index) {
              <div class="input-group mb-2">
                <span class="input-group-text"
                  ><i class="bi bi-shield-exclamation text-danger"></i
                ></span>
                <input
                  [id]="'allergy-' + $index"
                  class="form-control"
                  [formControlName]="$index"
                  placeholder="e.g. Penicillin, Peanuts"
                />
                <button
                  type="button"
                  class="btn btn-outline-danger"
                  (click)="removeAllergy($index)"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            }
            <button type="button" class="btn btn-outline-primary btn-sm" (click)="addAllergy()">
              <i class="bi bi-plus-circle me-1"></i>Add Allergy
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 2: Person Responsible for Paying Account -->
    <div class="card booking-card mb-4">
      <div class="card-header booking-card__header">
        <div>
          <div class="booking-card__eyebrow">Billing Guarantor</div>
          <h4 class="sub-title mb-0">Person Responsible for Account</h4>
        </div>
      </div>

      <div class="card-body p-4">
        <div class="form-check p-3 rounded bg-light border mb-3">
          <input
            class="form-check-input ms-0 me-2"
            type="checkbox"
            id="payerIsSameAsPatient"
            name="payerIsSameAsPatient"
            (change)="togglePayerIsSameAsPatient()"
          />
          <label class="form-check-label fw-bold text-dark" for="payerIsSameAsPatient">
            Patient is responsible for their own account (Self-paying)
          </label>
        </div>

        <div class="row g-3">
          <div class="col-md-6">
            <label for="payerFirstName" class="form-label">Guarantor First Name</label>
            <input id="payerFirstName" class="form-control" formControlName="payerFirstName" />
          </div>
          <div class="col-md-6">
            <label for="payerLastName" class="form-label">Guarantor Last Name</label>
            <input id="payerLastName" class="form-control" formControlName="payerLastName" />
          </div>
          <div class="col-md-6">
            <label for="payerEmail" class="form-label">Guarantor Email</label>
            <input id="payerEmail" class="form-control" formControlName="payerEmail" type="email" />
          </div>
          <div class="col-md-6">
            <label for="payerPhoneNumber" class="form-label">Guarantor Phone</label>
            <input id="payerPhoneNumber" class="form-control" formControlName="payerPhoneNumber" />
          </div>
          <div class="col-md-6">
            <label for="payerIdNumber" class="form-label">Guarantor ID Number</label>
            <input id="payerIdNumber" class="form-control" formControlName="payerIdNumber" />
          </div>
          <div class="col-md-6">
            <label for="payerEmployer" class="form-label">Employer</label>
            <input id="payerEmployer" class="form-control" formControlName="payerEmployer" />
          </div>
          <div class="col-md-12">
            <label for="payerAddressLine1" class="form-label">Billing Address</label>
            <input
              id="payerAddressLine1"
              class="form-control"
              formControlName="payerAddressLine1"
              placeholder="Address Line 1"
            />
          </div>
          <div class="col-md-6">
            <label for="payerCity" class="form-label">City</label>
            <input id="payerCity" class="form-control" formControlName="payerCity" />
          </div>
          <div class="col-md-6">
            <label for="payerPostalCode" class="form-label">Postal Code</label>
            <input id="payerPostalCode" class="form-control" formControlName="payerPostalCode" />
          </div>
        </div>
      </div>
    </div>

    <!-- Section 3: Emergency Contact -->
    <div class="card booking-card mb-4" [formGroup]="emergencyContactForm">
      <div class="card-header booking-card__header">
        <div>
          <div class="booking-card__eyebrow">Emergency Reachability</div>
          <h4 class="sub-title mb-0">Emergency Contact Details</h4>
        </div>
      </div>

      <div class="card-body p-4">
        <div class="form-check p-3 rounded bg-light border mb-3">
          <input
            class="form-check-input ms-0 me-2"
            type="checkbox"
            id="emergencyContactSameAsPayer"
            name="emergencyContactSameAsPayer"
            (change)="toggleEmergencyContactSameAsPayer()"
          />
          <label class="form-check-label fw-bold text-dark" for="emergencyContactSameAsPayer">
            Emergency contact is same as billing guarantor
          </label>
        </div>

        <div class="row g-3">
          <div class="col-md-6">
            <label for="emergencyFirstName" class="form-label"
              >First Name <span class="text-danger">*</span></label
            >
            <input id="emergencyFirstName" class="form-control" formControlName="firstName" />
            @if (
              emergencyContactForm.get('firstName')?.touched &&
              emergencyContactForm.get('firstName')?.invalid
            ) {
              <div class="text-danger small mt-1">First name is required.</div>
            }
          </div>
          <div class="col-md-6">
            <label for="emergencyLastName" class="form-label"
              >Last Name <span class="text-danger">*</span></label
            >
            <input id="emergencyLastName" class="form-control" formControlName="lastName" />
            @if (
              emergencyContactForm.get('lastName')?.touched &&
              emergencyContactForm.get('lastName')?.invalid
            ) {
              <div class="text-danger small mt-1">Last name is required.</div>
            }
          </div>
          <div class="col-md-4">
            <label for="emergencyEmail" class="form-label"
              >Email <span class="text-danger">*</span></label
            >
            <input id="emergencyEmail" class="form-control" type="email" formControlName="email" />
            @if (
              emergencyContactForm.get('email')?.touched &&
              emergencyContactForm.get('email')?.invalid
            ) {
              <div class="text-danger small mt-1">Valid email is required.</div>
            }
          </div>
          <div class="col-md-4">
            <label for="emergencyPhoneNumber" class="form-label"
              >Phone Number <span class="text-danger">*</span></label
            >
            <input id="emergencyPhoneNumber" class="form-control" formControlName="phoneNumber" />
            @if (
              emergencyContactForm.get('phoneNumber')?.touched &&
              emergencyContactForm.get('phoneNumber')?.invalid
            ) {
              <div class="text-danger small mt-1">Phone number is required.</div>
            }
          </div>
          <div class="col-md-4">
            <label for="emergencyRelationship" class="form-label"
              >Relationship <span class="text-danger">*</span></label
            >
            <select id="emergencyRelationship" class="form-select" formControlName="relationship">
              <option value="">Select Relationship</option>
              @for (relationship of emergencyContactRelationships; track relationship) {
                <option [value]="relationship">{{ relationship }}</option>
              }
            </select>
            @if (
              emergencyContactForm.get('relationship')?.touched &&
              emergencyContactForm.get('relationship')?.invalid
            ) {
              <div class="text-danger small mt-1">Relationship is required.</div>
            }
          </div>
        </div>
      </div>
    </div>

    <!-- Actions Sticky Bar -->
    <div class="actions actions--sticky d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center gap-3">
        <button type="button" class="btn btn-outline-secondary px-4" (click)="cancel()">
          Cancel
        </button>
        <small class="text-muted"
          >Fields marked with <span class="text-danger">*</span> are mandatory.</small
        >
      </div>

      <button
        type="submit"
        class="btn btn-primary px-4"
        [disabled]="
          isLoading || patientForm.invalid || (!isEditMode && emergencyContactForm.invalid)
        "
      >
        @if (isLoading) {
          <span class="spinner-border spinner-border-sm me-2" role="status"></span>Saving...
        } @else {
          <i class="bi bi-check2-circle me-1"></i>
          {{ isEditMode ? 'Update Patient File' : 'Save Patient File' }}
        }
      </button>
    </div>
  </form>
</div>
